import { PreBook } from 'domains/signupDirect/schemas/preBookSchema';
import { AddRecurringAppointmentsDto } from 'domains/add-recurring-sts/queries';
import { TimeZone, TimezoneOption, DEFAULT_TIMEZONE_OPTION } from 'domains/timezone';

type AppointmentPreference = {
  firstAppointment: { date: string; timezone: TimeZone };
  secondAppointment: { date: string; timezone: TimeZone };
};

export const createAppointmentData = (
  schedulePreference: AppointmentPreference,
  sessionSchedule: PreBook,
  displayTimezone: TimezoneOption | undefined,
  singleSessionOnly: boolean
): AddRecurringAppointmentsDto => {
  // Validate required inputs
  if (!schedulePreference?.firstAppointment?.date) {
    throw new Error('First appointment date is required');
  }

  if (!sessionSchedule) {
    throw new Error('Session schedule data is required');
  }

  // Validate required session schedule fields
  const requiredFields = [
    'therapistCalendarID',
    'clientFirstName',
    'clientLastName',
    'contactEmail',
    'contactPhone',
    'therapistEmail',
  ] as const;

  requiredFields.forEach(field => {
    if (!sessionSchedule[field]) {
      throw new Error(`Missing required field: ${field}`);
    }
  });

  try {
    // Validate dates
    const firstAppointmentDate = new Date(schedulePreference.firstAppointment.date);
    if (isNaN(firstAppointmentDate.getTime())) {
      throw new Error('Invalid first appointment date');
    }

    let secondAppointmentDate: Date | undefined;
    if (!singleSessionOnly && schedulePreference.secondAppointment.date) {
      secondAppointmentDate = new Date(schedulePreference.secondAppointment.date);
      if (isNaN(secondAppointmentDate.getTime())) {
        throw new Error('Invalid second appointment date');
      }
    }

    // Create appointment data with validated values
    const appointmentData: AddRecurringAppointmentsDto = {
      dates: [{ dateTime: firstAppointmentDate }],
      calendarID: sessionSchedule.therapistCalendarID,
      clientFirstName: sessionSchedule.clientFirstName,
      clientLastName: sessionSchedule.clientLastName,
      contactEmail: sessionSchedule.contactEmail,
      contactPhone: sessionSchedule.contactPhone,
      contactTimeZone: displayTimezone?.resolvedName ?? DEFAULT_TIMEZONE_OPTION.resolvedName,
      userTimezone: displayTimezone?.value ?? DEFAULT_TIMEZONE_OPTION.value,
      therapistEmail: sessionSchedule.therapistEmail,
    };

    // Add second appointment if applicable
    if (!singleSessionOnly && secondAppointmentDate) {
      appointmentData.dates.push({
        dateTime: secondAppointmentDate,
      });
    }

    return appointmentData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error creating appointment data:', error);
    throw error;
  }
};